import { HELP_ARTICLES, type HelpArticle } from "@/lib/help-content";
import { SM_FEATURES } from "@/lib/feature-catalog";

const byId = new Map(HELP_ARTICLES.map((a) => [a.id, a]));
const featureById = new Map(SM_FEATURES.map((f) => [f.id, f]));

function pick(ids: string[]): HelpArticle[] {
  return ids.map((id) => byId.get(id)).filter((a): a is HelpArticle => !!a);
}

const ROUTE_HELP_MAP: { prefix: string; articleIds: string[] }[] = [
  { prefix: "/app/pagos", articleIds: ["payment-center-overview", "payway-connect", "mercadopago-connect"] },
  { prefix: "/app/settings", articleIds: ["storefront-branding", "custom-domain-setup", "publish", "team"] },
  { prefix: "/app/productos", articleIds: ["products", "categories", "personalizacion-por-producto"] },
  { prefix: "/app/categorias", articleIds: ["categories", "products"] },
  { prefix: "/app/pedidos", articleIds: ["new-order", "faq-cancel"] },
  { prefix: "/app/pesaje", articleIds: ["pesaje-setup", "weighing"] },
  { prefix: "/app/dashboard", articleIds: ["dashboard", "publish"] },
  { prefix: "/app/onboarding", articleIds: ["onboarding", "products"] },
  { prefix: "/app/sucursales", articleIds: ["branches"] },
  { prefix: "/app/bundles", articleIds: ["products"] },
  { prefix: "/app/ingredientes", articleIds: ["products", "catalogacion-hub"] },
  { prefix: "/app/combos", articleIds: ["products", "categories"] },
  { prefix: "/app/marcas", articleIds: ["brands", "catalogacion-hub"] },
  { prefix: "/app/catalogacion/listas-precios", articleIds: ["price-lists", "catalogacion-hub"] },
  { prefix: "/app/catalogacion", articleIds: ["catalogacion-hub", "nicho-catalogo", "merchant-config"] },
  { prefix: "/app/leads", articleIds: ["leads-age-gate"] },
  { prefix: "/app/experience", articleIds: ["leads-age-gate", "storefront-branding"] },
  { prefix: "/app/merchant/cotizaciones", articleIds: ["merchant-cotizaciones"] },
  { prefix: "/app/merchant/config", articleIds: ["merchant-config"] },
  // Renata sub-rutas
  { prefix: "/app/renata", articleIds: ["renata-os-overview", "renata-agents"] },
  { prefix: "/app/renata/agents", articleIds: ["renata-agents", "renata-os-overview"] },
  { prefix: "/app/renata/knowledge", articleIds: ["renata-knowledge", "renata-os-overview"] },
  { prefix: "/app/renata/packs", articleIds: ["renata-packs", "renata-os-overview"] },
  { prefix: "/app/renata/automatizaciones", articleIds: ["renata-automation", "renata-knowledge"] },
  { prefix: "/app/renata/store", articleIds: ["renata-store", "renata-packs"] },
  { prefix: "/app/customer-ai", articleIds: ["renata-agents", "renata-os-overview"] },
  // Hardware
  { prefix: "/app/hardware", articleIds: ["hardware-hub", "hardware-setup"] },
  // Rutas sin cobertura previa
  { prefix: "/app/campaigns", articleIds: ["email-automations", "email-notif"] },
  { prefix: "/app/clientes", articleIds: ["clientes-hub", "cuenta-cliente"] },
  { prefix: "/app/inventario", articleIds: ["inventory-hub", "control-stock"] },
  { prefix: "/app/pos", articleIds: ["ordenes-hub", "facturacion-hub"] },
  { prefix: "/app/salon", articleIds: ["salon-qr", "salon-split-check"] },
  { prefix: "/app/facturacion", articleIds: ["facturacion-hub"] },
];

export function getContextualHelpArticles(ctx: { pathname: string; productFlags?: Record<string, boolean> }, limit = 4): HelpArticle[] {
  const path = ctx.pathname.split("?")[0];
  const match = ROUTE_HELP_MAP.find((r) => path === r.prefix || path.startsWith(`${r.prefix}/`));
  return (match ? pick(match.articleIds) : pick(["dashboard", "onboarding"])).slice(0, limit);
}

const FEATURE_UPGRADE_HINTS: Record<string, { featureId: string; title: string; planHint: string }> = {
  custom_domain: { featureId: "custom_domain", title: "Dominio propio", planHint: "Disponible desde plan Business" },
  payment_payway: { featureId: "payment_payway", title: "Payway", planHint: "Verificá tu plan o contactá soporte" },
  payment_mercadopago: { featureId: "payment_mercadopago", title: "Mercado Pago", planHint: "Incluido según plan activo" },
  payment_modo: { featureId: "payment_modo", title: "MODO QR", planHint: "Disponible en planes superiores" },
};

export function getFeatureUpgradeHints(flags: Record<string, boolean> | undefined): typeof FEATURE_UPGRADE_HINTS[string][] {
  if (!flags) return [];
  return Object.values(FEATURE_UPGRADE_HINTS).filter((h) => flags[h.featureId] === false);
}

export function resolveHelpArticleById(id: string): HelpArticle | undefined {
  return byId.get(id);
}

/**
 * SCRUM-245 — único mecanismo canónico para ir de una feature del catálogo
 * central (`SM_FEATURES`) a su artículo de ayuda. `HelpLink` (y cualquier
 * lugar que muestre una feature con su `docsSlug`) debe pasar por acá en vez
 * de resolver `docsSlug` a mano: así un `docsSlug` roto o una feature sin
 * documentación no llegan nunca a un link muerto, sólo a "sin ayuda" (undefined).
 */
export function resolveFeatureHelpArticleId(featureId: string): string | undefined {
  const docsSlug = featureById.get(featureId)?.docsSlug;
  if (!docsSlug) return undefined;
  return byId.has(docsSlug) ? docsSlug : undefined;
}

export function docsDeepLink(articleId: string, base = "/docs"): string {
  return `${base}?article=${encodeURIComponent(articleId)}`;
}
